import React from 'react';
import { useTranslations } from 'next-intl';
import { ThemeProvider } from '@emotion/react';

import {
  ContentLayout,
  HomePageContent,
} from '@silvertours/common-landingpages-view';
import {
  BenefitsLegacy,
  ContentArticle,
  NewsletterRegistrationLegacy,
  OffersModule,
  SerpOffersModule,
  StageLegacy,
  TrustLegacy,
  AppInstallBanner,
  useShowAppBanner,
} from '@silvertours/front-features';
import { Theme } from '@silvertours/front-shared';

import { Runtime } from '@silvertours/front-entities';
import { Body } from '../body';

const HomeContent = ({
  content: {
    article,
    globalRating,
    introduction,
    links,
    offersByCategory,
    stage,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<HomePageContent>) => {
  const t = useTranslations('features');
  const translations = t.raw('article' as any);
  const offersTranslations = useTranslations('features.offers');
  const { language } = Runtime.useLocale();
  const appBannerState = useShowAppBanner(language);

  return (
    <>
      <Body
        title={primaryHeadline}
        stage={<StageLegacy.Stage content={stage.content} />}
        content={({ containerModule }) => [
          containerModule(
            'usp',
            <BenefitsLegacy.Usp rating={globalRating.content} />,
          ),
          containerModule(
            'introduction-content',
            <ThemeProvider theme={Theme.defaultTheme}>
              <ContentArticle
                modules={introduction?.segments ?? []}
                translations={translations}
              />
            </ThemeProvider>,
            !!introduction?.segments,
          ),
          containerModule(
            'serpOffers',
            <ThemeProvider theme={Theme.defaultTheme}>
              {!!offersByCategory.content && (
                <SerpOffersModule
                  content={offersByCategory.content}
                  translations={{
                    airConditioning: offersTranslations('car.airConditioning'),
                    carTypes: t.raw('offers.car.type' as any),
                    prevText: offersTranslations('prevText'),
                    nextText: offersTranslations('nextText'),
                    searchButtonText: offersTranslations('searchButtonText'),
                    disclaimertext: offersTranslations('disclaimerSerpOffers'),
                    pricePrefix: offersTranslations('pricePrefix'),
                    priceSuffix: offersTranslations('priceSuffix'),
                    serpTitle: offersTranslations('serp.title'),
                    transmissionAutomatic: offersTranslations(
                      'car.transmissionAutomatic',
                    ),
                    transmissionManual: offersTranslations(
                      'car.transmissionManual',
                    ),
                  }}
                />
              )}
            </ThemeProvider>,
            !!offersByCategory.content &&
              Object.keys(offersByCategory.content).length > 0,
          ),
          containerModule(
            'offers',
            <ThemeProvider theme={Theme.defaultTheme}>
              {!!offersByCategory.content && (
                <OffersModule
                  content={offersByCategory.content}
                  translations={{
                    badgeText: offersTranslations('badge'),
                    disclaimer: offersTranslations('disclaimer'),
                    disclaimerOverlayCTA: offersTranslations(
                      'disclaimerOverlayCTA',
                    ),
                    durationFrom: offersTranslations('durationFrom'),
                    headline: offersTranslations('headline'),
                    pricePrefix: offersTranslations('pricePrefix'),
                    priceSuffix: offersTranslations('priceSuffix'),
                    searchButtonText: offersTranslations('searchButtonText'),
                    offerDetailsTitle: offersTranslations('offerDetailsTitle'),
                    carTypes: t.raw('offers.car.type' as any),
                  }}
                />
              )}
            </ThemeProvider>,
            !!offersByCategory.content &&
              Object.keys(offersByCategory.content).length > 0,
          ),
          containerModule('test-winner', <TrustLegacy.CarigamiGarantie />),
          containerModule(
            'newsletter',
            <NewsletterRegistrationLegacy.NewsletterRegistration type="index" />,
          ),
          containerModule(
            'article',
            <ThemeProvider theme={Theme.defaultTheme}>
              <ContentArticle
                modules={article.segments}
                translations={translations}
              />
            </ThemeProvider>,
          ),
          containerModule(
            'linkLists',
            <ThemeProvider theme={Theme.defaultTheme}>
              <ContentArticle
                modules={links.content ?? []}
                translations={translations}
              />
            </ThemeProvider>,
            !!links.content.length,
          ),
        ]}
        {...commonProps}
      />

      {/* app install banner */}
      <div id="fglayer" role="application" />
      <AppInstallBanner {...appBannerState} />
    </>
  );
};

export { HomeContent };
